import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import RoundedButton from '../Buttons/RoundedButton';
import { useDispatch } from 'react-redux';
import { updateCreditPlanDebugMode } from '@/redux/slices/creditPlanSlice';
import Shimmer from '../Shimmer';
import { CreditPlanResponseModel, CreditPlanType } from '@/@types/client-api';
import classnames from 'classnames';
import { useDownloadCreditPlanDatasetCsvMutation } from '@/redux/api/client/creditPlan';
import Toggle from '../Form/Toggle';
import Text from '@/components/cms/Text';

type DebugButtonsProps = {
  allowed: boolean;
  isActive: boolean;
  planId?: string;
  isDeleting?: boolean;
  onDeletePress?: () => void;
  onPdfPreviewToggle?: (val: boolean) => void;
  planItem?: CreditPlanResponseModel;
  planType?: CreditPlanType;
};

const DebugButtons: FC<DebugButtonsProps> = ({
  allowed,
  isActive,
  planId,
  isDeleting,
  onDeletePress,
  onPdfPreviewToggle,
  planItem,
  planType
}) => {
  const dispatch = useDispatch();
  const [downloadCreditPlanDatasetCsv] = useDownloadCreditPlanDatasetCsvMutation();
  const hasDebugActions = !!planId;
  const [showDebugActions, setShowDebugActions] = useState<boolean>(!!isActive && hasDebugActions);
  const [isAnimatingDebugActions, setIsAnimatingDebugActions] = useState<boolean>(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState<boolean>(false);
  const [isPdfPreviewMode, setIsPdfPreviewMode] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && hasDebugActions) {
      setShowDebugActions(true);
      setIsAnimatingDebugActions(true);

      const timeoutId = window.setTimeout(() => {
        setIsAnimatingDebugActions(false);
      }, 300);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    setIsAnimatingDebugActions(true);

    const timeoutId = window.setTimeout(() => {
      setShowDebugActions(false);
      setIsAnimatingDebugActions(false);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasDebugActions, isActive]);

  const buttonShimmer = useMemo(() => {
    return <Shimmer className="size-10 rounded-full" />;
  }, []);

  const onPdfPreviewToggleChange = useCallback(
    (val: boolean) => {
      setIsPdfPreviewMode(val);
      onPdfPreviewToggle?.(val);
    },
    [onPdfPreviewToggle]
  );

  useEffect(() => {
    if (!isActive && isPdfPreviewMode) onPdfPreviewToggleChange(false);
  }, [isActive, isPdfPreviewMode, onPdfPreviewToggleChange]);

  if (!allowed) return;

  if (isDeleting)
    return (
      <>
        <div className="mr-auto flex flex-row items-center justify-start">
          <Shimmer className="mr-2 size-10 rounded-full" />
          <Shimmer className="mr-2 size-10 rounded-full" />
          <Shimmer className="size-10 rounded-full" />
        </div>
      </>
    );

  const handleDownloadJson = () => {
    if (!planItem) return;
    const json = JSON.stringify(planItem, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const filename = `creditplan_${planItem?.licenseIdentifier || planId || 'unknown'}.json`;
    // Native browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  const handleDownloadCsv = async () => {
    if (!planItem?.jurisdiction) return;

    setIsDownloadingCSV(true);
    await downloadCreditPlanDatasetCsv({
      jurisdiction: planItem.jurisdiction,
      planId: planId ?? planItem.processId ?? planItem?.creditPlan?.id ?? '',
      licenseIdentifier: planItem?.licenseIdentifier,
      creditPlanType: planType
    });
    setIsDownloadingCSV(false);
  };

  return (
    <div className="mr-auto flex flex-row items-center justify-start">
      <RoundedButton
        icon="info"
        buttonSize="large"
        title="Toggle debug mode"
        color={!isActive ? 'outline-black' : 'gray-darker'}
        className="rounded hover:shadow-lg"
        onClick={() => {
          dispatch(updateCreditPlanDebugMode(!isActive));
        }}
      />
      {hasDebugActions && showDebugActions && (
        <div className="relative ml-3 overflow-hidden">
          {isAnimatingDebugActions && (
            <div
              className="pointer-events-none absolute inset-y-0
              left-0 z-1 w-4 animate-fadeIn bg-gradient-to-r from-gray/25
              to-transparent"
            />
          )}
          <div
            className={classnames('flex flex-row items-center justify-start gap-3 duration-long', {
              'animate-in slide-in-from-left': !!isActive,
              'animate-out slide-out-to-left': !isActive
            })}
          >
            {!!onDeletePress && (
              <RoundedButton
                color="red"
                icon="delete"
                buttonSize="large"
                title="Delete credit plan"
                onClick={() => onDeletePress()}
              />
            )}
            <RoundedButton
              color="black"
              icon="download"
              buttonSize="large"
              title="Download creditplan JSON"
              onClick={handleDownloadJson}
            />
            {isDownloadingCSV ? (
              buttonShimmer
            ) : (
              <RoundedButton
                color="black"
                icon="download-csv"
                buttonSize="large"
                title="Download pre-LLM dataset CSV"
                onClick={handleDownloadCsv}
              />
            )}
            {planType == CreditPlanType.PERSONALIZED && (
              <div className="flex flex-col flex-nowrap items-start justify-start">
                <Toggle
                  variant="color"
                  title="Toggle PDF preview"
                  checked={isPdfPreviewMode}
                  onCheckedChange={onPdfPreviewToggleChange}
                  id="DebugPdfPreviewToggle"
                />
                <Text className="label mt-1">PDF Preview</Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugButtons;
